// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// Plain-English label + format helpers for the Investment Reporting review shell.
// Colour ALWAYS rides on a semantic token (never a hardcoded hex) so light/dark theme and
// the ChartContainer CSS-var path stay congruent. Mirrors research-hub/labels.ts.

import type {
  AnyCell,
  Cell,
  Confidence,
  CycleStatus,
  LegacyTier,
  ProvenanceEngine,
  ReportingTier,
} from "@/types/ir";

export type Tone = "neutral" | "good" | "attention" | "bad" | "quarantine";

// ============================================================================
// The cycle's PRIMARY PERIOD KEY.
// ============================================================================
// Every governed cell is stamped under a period key, and the producer chooses that key from
// the cycle's period_type: services/reporting-compute/app.py:710
//
//     primary_period = "m1" if req.period_type == "monthly-interim" else "q2"
//
// A reader that hardcodes "q2" therefore asks a monthly-interim cycle for a window it never
// computed, and every lookup misses — rendering a wall of not_found sentinels over cells
// that are present and `ok` under "m1". This helper is the ONE place the mapping lives, so
// the read side cannot drift from the write side again. Keep it in lockstep with app.py:710.
export type PeriodKey = "m1" | "q2" | "m6" | "y1";

export function primaryPeriodKey(periodType: string | null | undefined): PeriodKey {
  return periodType === "monthly-interim" ? "m1" : "q2";
}

// Reader-facing label for a period key. The matrices and the excess chart print this beside
// the figures so a reader can never mistake WHICH window they are looking at — the defect
// that hid this bug was a heading reading "Q2" over cells the cycle never had.
export function periodLabel(period: PeriodKey): string {
  switch (period) {
    case "m1":
      return "1 month";
    case "q2":
      return "Q2";
    case "m6":
      return "6 months";
    case "y1":
      return "1 year";
  }
}

// Token-only tone -> text colour. No hex anywhere.
export function toneText(tone: Tone): string {
  switch (tone) {
    case "good":
      return "text-primary";
    case "attention":
      return "text-amber-600 dark:text-amber-400";
    case "bad":
      return "text-destructive";
    case "quarantine":
      return "text-muted-foreground";
    default:
      return "text-foreground";
  }
}

export function toneDot(tone: Tone): string {
  switch (tone) {
    case "good":
      return "bg-primary";
    case "attention":
      return "bg-amber-500";
    case "bad":
      return "bg-destructive";
    case "quarantine":
      return "bg-muted-foreground";
    default:
      return "bg-muted-foreground";
  }
}

// Format a numeric Cell value with a basis-aware unit. Excess/pp values show an explicit
// sign so a reader never mistakes a lag for a lead.
export function formatCellValue(value: number | null | undefined, basis: string, digits = 2): string {
  if (value == null || Number.isNaN(value)) return "—";
  const b = (basis || "").toLowerCase();
  const isPp = b.includes("pp") || b.includes("/pp");
  const isPct = b.includes("%") || b.includes("weight");
  const signed = isPp ? (value >= 0 ? "+" : "") : "";
  const unit = isPp ? "pp" : isPct ? "%" : "";
  return `${signed}${value.toFixed(digits)}${unit}`;
}

// The sign tone for an excess/drift/attribution cell (positive = good, negative = bad).
export function signTone(value: number | null | undefined): Tone {
  if (value == null || Number.isNaN(value)) return "neutral";
  if (value > 0) return "good";
  if (value < 0) return "bad";
  return "neutral";
}

export function engineLabel(engine: ProvenanceEngine | string | undefined): string {
  switch (engine) {
    case "fe_golden_csv":
      return "FE golden CSV";
    case "deterministic_compute":
      return "Deterministic compute";
    case "supabase_pit_reconstruction":
      return "Point-in-time reconstruction";
    case "web_verified_market":
      return "Web-verified market";
    case "vendor_actual":
      return "Vendor actuals (workbook)";
    case "vendor_chart_verified":
      return "Vendor chart (human-verified)";
    case "manual_locked":
      return "Manually locked";
    default:
      return engine ? String(engine) : "—";
  }
}

export function confidenceLabel(c: Confidence | string | undefined): string {
  switch (c) {
    case "high":
      return "High confidence";
    case "medium":
      return "Medium confidence";
    case "low":
      return "Low confidence";
    default:
      return "—";
  }
}

export function confidenceTone(c: Confidence | string | undefined): Tone {
  switch (c) {
    case "high":
      return "good";
    case "medium":
      return "attention";
    case "low":
      return "bad";
    default:
      return "neutral";
  }
}

// Old 2-value fixture tier -> the new 3-value domain. Fail-closed: unknown -> most restrictive.
export function normaliseTier(tier: LegacyTier | ReportingTier | string | undefined): ReportingTier {
  switch (tier) {
    case "retail_public":
      return "retail_public";
    case "adviser_restricted":
      return "adviser_restricted";
    case "ic_internal":
      return "ic_internal";
    // legacy scheme
    case "client_private":
      return "adviser_restricted";
    case "proprietary":
      return "ic_internal";
    default:
      return "ic_internal";
  }
}

export function tierLabel(tier: ReportingTier): string {
  switch (tier) {
    case "retail_public":
      return "Retail — public";
    case "adviser_restricted":
      return "Adviser — restricted";
    case "ic_internal":
      return "IC — internal";
  }
}

export function tierTone(tier: ReportingTier): Tone {
  switch (tier) {
    case "retail_public":
      return "good";
    case "adviser_restricted":
      return "attention";
    case "ic_internal":
      return "bad"; // most restrictive — flagged the strongest
  }
}

export function statusLabel(s: CycleStatus | string | undefined): string {
  switch (s) {
    case "draft":
      return "Draft";
    case "computed":
      return "Computed";
    case "verified":
      return "Verified";
    case "gated":
      return "Gated";
    case "rendered":
      return "Rendered";
    case "delivered":
      return "Delivered";
    case "failed":
      return "Failed";
    default:
      return s ? String(s) : "—";
  }
}

export function statusTone(s: CycleStatus | string | undefined): Tone {
  switch (s) {
    case "delivered":
    case "gated":
      return "good";
    case "verified":
    case "computed":
    case "rendered":
      return "attention";
    case "failed":
      return "bad";
    default:
      return "neutral";
  }
}

export function formatDay(d: string | null | undefined): string {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// ----------------------------------------------------------------------------
// Golden-CSV drop vocabulary — the `kind` values reporting-golden-ingest accepts, in
// PLAIN ENGLISH. The raw enum is never shown to a human: "fe_benchmark_constituent_weights"
// is not a sentence, and picking the wrong kind is the single most consequential mistake at
// the drop point (kind selects the parser, the header contract, the crosswalk and the
// expected roster). MIRRORS the KINDS array in
// supabase/functions/reporting-golden-ingest/index.ts:178 — exactly these twelve, in that
// order of authority. If that list changes, this must change with it.
//
// ⚠️ `fe_ia_sector_returns` is the TWELFTH and newest member, and that literal is EXACT — it is
// set by supabase/migrations/20260811120000_widen_golden_kinds.sql. A CHECK constraint gives no
// near-miss diagnostic, so declaring `ia_sector_returns` instead would fail with a bare 23514 and
// land nothing at all. This list, that CHECK and index.ts:178/369 ship together or not at all.
// ----------------------------------------------------------------------------
export const GOLDEN_KINDS = [
  "fe_portfolio_returns",
  "fe_fund_returns",
  "fe_benchmark_returns",
  "fe_ia_sector_returns",
  "fe_benchmark_constituent_weights",
  "fe_sector_weights",
  "holdings_base_snapshot",
  "holdings_deltas",
  "ba_trades",
  "sector_mappings",
  "composite_constituents",
  "costs",
] as const;

export type GoldenKind = (typeof GOLDEN_KINDS)[number];

export interface GoldenKindSpec {
  kind: GoldenKind;
  label: string;
  hint: string;
  group: string;
}

// Grouped for the picker. The FE performance exports come first because they are what gets
// dropped every cycle; the reference kinds change rarely.
export const GOLDEN_KIND_SPECS: GoldenKindSpec[] = [
  {
    kind: "fe_portfolio_returns",
    label: "Portfolio returns (P1–P5 / R1–R5)",
    hint: "The FE Performance Table export, read for the model ladder.",
    group: "Performance — FE exports",
  },
  {
    kind: "fe_benchmark_returns",
    label: "Benchmark returns (CBAM I–V / AFI / ARC)",
    hint: "Usually the SAME file as portfolio returns, dropped a second time under this kind — one export carries both.",
    group: "Performance — FE exports",
  },
  {
    kind: "fe_fund_returns",
    label: "Fund-level returns",
    hint: "Performance of the individual funds held inside the portfolios.",
    group: "Performance — FE exports",
  },
  {
    kind: "fe_benchmark_constituent_weights",
    label: "Benchmark constituent weights",
    hint: "What sits inside each benchmark, and in what proportion.",
    group: "Performance — FE exports",
  },
  {
    kind: "fe_sector_weights",
    label: "Sector weights",
    hint: "Sector-level exposure per portfolio.",
    group: "Performance — FE exports",
  },
  {
    kind: "holdings_base_snapshot",
    label: "Holdings — opening snapshot",
    hint: "Where each portfolio started the window (base weights).",
    group: "Holdings & trades",
  },
  {
    kind: "holdings_deltas",
    label: "Holdings — changes during the period",
    hint: "Weight changes applied across the window (deltas, not levels).",
    group: "Holdings & trades",
  },
  {
    kind: "ba_trades",
    label: "Broker / administrator trades",
    hint: "The trade file used to corroborate the holdings changes.",
    group: "Holdings & trades",
  },
  {
    kind: "composite_constituents",
    label: "Composite constituents (the nine MPS houses)",
    hint: "The breakdown behind the CBAM I–V internal benchmark.",
    group: "Reference data",
  },
  {
    kind: "fe_ia_sector_returns",
    label: "IA sector returns (the 57 category means)",
    hint: "Investment Association category returns, equal-weighted and net of OCF. Landed under FE's own sector names — the crosswalk to CBAM sectors is a separate, later decision.",
    group: "Performance — FE exports",
  },
  {
    kind: "sector_mappings",
    label: "Sector mappings",
    hint: "Fund → sector lookup used by the attribution.",
    group: "Reference data",
  },
  {
    kind: "costs",
    label: "Costs",
    hint: "Charges applied in the return calculation.",
    group: "Reference data",
  },
];

// Stable group order for the picker (derived from the spec list, first-seen order).
export const GOLDEN_KIND_GROUPS: string[] = GOLDEN_KIND_SPECS.reduce<string[]>((acc, s) => {
  if (!acc.includes(s.group)) acc.push(s.group);
  return acc;
}, []);

export function goldenKindSpec(kind: string | null | undefined): GoldenKindSpec | undefined {
  return GOLDEN_KIND_SPECS.find((s) => s.kind === kind);
}

// Plain-English name for a kind. Falls back to the raw token rather than inventing a label —
// an unrecognised kind must read as unrecognised, never as something friendly and wrong.
export function goldenKindLabel(kind: string | null | undefined): string {
  return goldenKindSpec(kind)?.label ?? (kind ? String(kind) : "—");
}

// The `basis` domain reporting-golden-ingest enforces at GATE 2 (index.ts:193). GBP is the
// default because every FE export CBAM takes is priced in sterling — and on the FE-native
// path GATE 6 hard-compares this against the file's own "Currency:" preamble through a
// CLOSED lookup, so a wrong pick here is REJECTED, never silently accepted.
export const GOLDEN_BASES: { value: string; label: string }[] = [
  { value: "GBP", label: "GBP — pounds sterling" },
  { value: "USD", label: "USD" },
  { value: "local", label: "Local currency" },
  { value: "mixed_declared", label: "Mixed (declared)" },
];

// Severity token (from the live reporting_gate_policy row, so treat it as an open string)
// -> the shared tone vocabulary.
export function ingestSeverityTone(severity: string | null | undefined): Tone {
  switch ((severity ?? "").toLowerCase()) {
    case "info":
      return "neutral";
    case "warn":
    case "warning":
      return "attention";
    case "error":
    case "critical":
      return "bad";
    default:
      return "neutral";
  }
}

// A stable, brand-neutral chart palette expressed as HSL over theme tokens — NEVER hex.
// Feed these into ChartContainer's `config[key].color`; ChartContainer injects them as
// --color-<key> and the recharts marks reference the token, so charts theme in dark mode.
export const CHART_TOKENS = [
  "hsl(var(--primary))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "hsl(var(--muted-foreground))",
];

// True iff a cell is a live, in-scope number safe to display as data.
export function isDisplayableNumber(cell: AnyCell | Cell | undefined): boolean {
  return !!cell && cell.status === "ok";
}

// ---------------------------------------------------------------------------
// Content-hash display. Hashes are IDENTITY here, not decoration: they are the
// thing a review row binds to, so they are shown in a stable, comparable form
// and NEVER silently truncated without the ellipsis that says so.
// ---------------------------------------------------------------------------
export function shortHash(h: string | null | undefined, chars = 12): string {
  if (!h) return "—";
  return h.length <= chars ? h : `${h.slice(0, chars)}…`;
}

// Plain-English rendering of the SERVER-owned newsletter review checklist keys
// (reporting_newsletter_review_checklist(), 0003i §2). The server owns WHICH keys
// exist; this only owns how a known key reads. An unknown key still renders — as
// its raw key with underscores relaxed — so a server that adds a tenth item is
// never silently dropped from the reviewer's list.
export function newsletterCheckLabel(key: string): string {
  switch (key) {
    case "read_in_full":
      return "I opened and read the rendered email end to end.";
    case "fair_clear_not_misleading":
      return "The copy is fair, clear and not misleading.";
    case "risk_warning_present":
      return "A risk warning is present.";
    case "past_performance_disclaimer_present":
      return "The past-performance disclaimer is present.";
    case "no_unexplained_or_bare_numbers":
      return "No bare or unexplained numbers — every figure is sourced or contextualised.";
    case "subject_line_and_sender_correct":
      return "The subject line, from-name and reply-to shown below are correct.";
    case "unsubscribe_and_permission_wording_present":
      return "Unsubscribe and permission wording is present.";
    case "firm_identification_correct":
      return "The firm is correctly identified.";
    case "audience_is_the_intended_retail_list":
      return "The audience id below is the intended retail list.";
    default:
      return key.replace(/_/g, " ");
  }
}

// Plain-English label for the envelope fields pinned into the freeze manifest.
export function envelopeFieldLabel(key: string): string {
  switch (key) {
    case "subject":
      return "Subject";
    case "from_name":
      return "From name";
    case "reply_to":
      return "Reply-to";
    case "audience_id":
      return "Audience id";
    default:
      return key.replace(/_/g, " ");
  }
}
